/**
 * CacheService — measures and cleans the derived-asset cache.
 */

import type { BlobStore } from '../adapters/opfs-store';
import type { MediaRepository } from '../repositories';

export interface CacheStats {
  entries: number;
  bytes: number;
  orphans: number;
  /** Browser-reported quota, when available. */
  quotaBytes: number | null;
  usageBytes: number | null;
}

export class CacheService {
  constructor(
    private readonly cache: BlobStore,
    private readonly media: MediaRepository,
  ) {}

  async stats(): Promise<CacheStats> {
    const keys = await this.cache.keys();
    const live = this.media.liveCacheKeys();

    let quotaBytes: number | null = null;
    let usageBytes: number | null = null;
    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
      const estimate = await navigator.storage.estimate();
      quotaBytes = estimate.quota ?? null;
      usageBytes = estimate.usage ?? null;
    }

    return {
      entries: keys.length,
      bytes: await this.cache.totalBytes(),
      orphans: keys.filter((key) => !live.has(key)).length,
      quotaBytes,
      usageBytes,
    };
  }

  /** Drops cache entries no row references any more. */
  async sweepOrphans(): Promise<{ removed: number }> {
    const keys = await this.cache.keys();
    const live = this.media.liveCacheKeys();

    let removed = 0;
    for (const key of keys) {
      if (live.has(key)) continue;
      await this.cache.delete(key);
      removed++;
    }
    return { removed };
  }

  /** Asks the browser not to evict our data under storage pressure. */
  async requestPersistence(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return navigator.storage.persist();
  }
}
